/**
 * Communication Metrics Dashboard
 * Shows WhatsApp/Call stats, response rates, and outcome breakdown
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  MessageCircle, Phone, TrendingUp, Clock, AlertTriangle, 
  CheckCircle, XCircle, Download, RefreshCw, Users, ArrowLeft,
  FileText, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CommunicationMetricsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [exporting, setExporting] = useState(false);
  
  const loadMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/fm/communication/metrics`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date_from: dateFrom, date_to: dateTo }
      });
      setMetrics(res.data);
    } catch (err) {
      console.error('Load metrics error:', err);
      toast.error('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, token]);
  
  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);
  
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/fm/communication/metrics/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { format, date_from: dateFrom, date_to: dateTo },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `communication_metrics_${dateFrom}_${dateTo}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };
  
  const outcomeColors = {
    confirmed: 'bg-emerald-100 text-emerald-700',
    no_answer: 'bg-amber-100 text-amber-700',
    quote_promised: 'bg-purple-100 text-purple-700',
    engineer_assigned: 'bg-blue-100 text-blue-700',
    delayed: 'bg-orange-100 text-orange-700',
    callback_required: 'bg-cyan-100 text-cyan-700',
    escalate: 'bg-red-100 text-red-700'
  };
  
  const actionTypeLabels = {
    chased_contractor: 'Chased Contractor',
    quote_requested: 'Quote Requested',
    contractor_confirmed: 'Contractor Confirmed',
    engineer_assigned: 'Engineer Assigned',
    attendance_confirmed: 'Attendance Confirmed',
    attendance_delayed: 'Attendance Delayed',
    no_response: 'No Response',
    escalated: 'Escalated',
    reassign_required: 'Reassign Required',
    client_updated: 'Client Updated',
    whatsapp_sent: 'WhatsApp Sent',
    call_made: 'Call Made'
  };
  
  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-[#1F7A78]" />
                Communication Metrics
              </h1>
              <p className="text-sm text-slate-500">Track WhatsApp, calls, and action outcomes</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleExport('csv')}
              disabled={exporting}
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleExport('pdf')}
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              <FileText className="h-4 w-4 mr-1" />
              PDF
            </Button>
          </div>
        </div>
        
        {/* Date Filter */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-end gap-4 flex-wrap">
              <div>
                <Label className="text-xs text-slate-500">From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-40"
                  data-testid="date-from-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-40"
                  data-testid="date-to-input"
                />
              </div>
              <Button 
                onClick={loadMetrics} 
                disabled={loading}
                className="bg-[#1F7A78] hover:bg-[#16615f]"
                data-testid="refresh-metrics-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              {/* Quick filters */}
              <div className="flex gap-2 ml-auto">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setDateFrom(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
                    setDateTo(format(new Date(), 'yyyy-MM-dd'));
                  }}
                >
                  Last 7 Days
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setDateFrom(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
                    setDateTo(format(new Date(), 'yyyy-MM-dd'));
                  }}
                >
                  Last 30 Days
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-4 border-[#1F7A78] border-t-transparent rounded-full" />
          </div>
        ) : metrics ? (
          <>
            {/* Today's Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">Today's WhatsApp</p>
                      <p className="text-3xl font-bold text-green-700">{metrics.today?.whatsapp || 0}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <MessageCircle className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-cyan-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Today's Calls</p>
                      <p className="text-3xl font-bold text-blue-700">{metrics.today?.calls || 0}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <Phone className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-indigo-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Actions Logged Today</p>
                      <p className="text-3xl font-bold text-purple-700">{metrics.today?.actions_logged || 0}</p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-full">
                      <CheckCircle className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Period Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Total Communications</p>
                  <p className="text-2xl font-bold text-slate-800">{metrics.summary?.total_communications || 0}</p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">WhatsApp</p>
                  <p className="text-2xl font-bold text-green-600">{metrics.summary?.total_whatsapp || 0}</p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Phone Calls</p>
                  <p className="text-2xl font-bold text-blue-600">{metrics.summary?.total_calls || 0}</p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Response Rate</p>
                  <p className="text-2xl font-bold text-[#1F7A78]">{metrics.summary?.response_rate_percent || 0}%</p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">No Response</p>
                  <p className="text-2xl font-bold text-amber-600">{metrics.summary?.total_no_response || 0}</p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Avg Response Time</p>
                  <p className="text-2xl font-bold text-slate-700">{metrics.summary?.avg_time_to_outcome_minutes || 0}m</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Detailed Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Outcome Breakdown */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-[#1F7A78]" />
                    Outcome Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {metrics.outcome_breakdown && Object.keys(metrics.outcome_breakdown).length > 0 ? (
                    Object.entries(metrics.outcome_breakdown).map(([outcome, count]) => (
                      <div key={outcome} className="flex items-center justify-between">
                        <span className={`text-sm px-2 py-1 rounded ${outcomeColors[outcome] || 'bg-slate-100 text-slate-700'}`}>
                          {outcome.replace(/_/g, ' ')}
                        </span>
                        <span className="font-semibold text-slate-700">{count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No outcomes recorded</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Action Type Breakdown */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#1F7A78]" />
                    Action Types
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {metrics.action_type_breakdown && Object.keys(metrics.action_type_breakdown).length > 0 ? (
                    Object.entries(metrics.action_type_breakdown)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 8)
                      .map(([atype, count]) => (
                        <div key={atype} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">
                            {actionTypeLabels[atype] || atype.replace(/_/g, ' ')}
                          </span>
                          <span className="font-semibold text-slate-700">{count}</span>
                        </div>
                      ))
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No actions logged</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Top Contractors */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#1F7A78]" />
                    Top Contractors (by communication)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {metrics.top_contractors && metrics.top_contractors.length > 0 ? (
                    metrics.top_contractors.map((contractor, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-[#1F7A78] text-white text-xs flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span className="text-sm text-slate-600 truncate max-w-[150px]">
                            {contractor.name}
                          </span>
                        </div>
                        <span className="font-semibold text-slate-700">{contractor.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No contractor data</p>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Escalation Stats */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Escalation Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 uppercase">No Response</p>
                    <p className="text-2xl font-bold text-amber-700">{metrics.summary?.total_no_response || 0}</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-xs text-red-600 uppercase">Escalated</p>
                    <p className="text-2xl font-bold text-red-700">{metrics.summary?.total_escalated || 0}</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600 uppercase">With Outcome</p>
                    <p className="text-2xl font-bold text-emerald-700">{metrics.summary?.total_with_outcome || 0}</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-600 uppercase">Pending Outcome</p>
                    <p className="text-2xl font-bold text-slate-700">
                      {(metrics.summary?.total_communications || 0) - (metrics.summary?.total_with_outcome || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-20 text-slate-500">
            No metrics data available
          </div>
        )}
      </div>
    </div>
  );
}
